/* Slice VI-02 · Contractor Measurement Decision and Promotion Delta.
 *
 * Additive section mounted inside the existing WorkAreas card. Surfaces
 * pending VI-01 measurement candidates for contractor review WITHIN the
 * Slice 2H WorkAreas experience — never as a parallel page. Provides
 * the five contractor decision actions:
 *
 *   Accept · Correct · Reject · Request recapture · Leave preliminary
 *
 * All consequential actions gate behind an explicit contractor
 * confirmation. Nothing here creates estimates, proposals, contracts,
 * invoices, prices, or customer-facing scope — those belong to VI-03.
 *
 * Product hierarchy reinforced by the UI:
 *   1. Levi-guided video and voice walkthrough — PRIMARY.
 *   2. Still photographs — SECONDARY supporting evidence.
 *   3. Manual contractor entry / correction — CONTROLLING contractor
 *      decision.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import {
  Video,
  Camera,
  Mic,
  Calculator,
  CheckCircle2,
  Pencil,
  XCircle,
  RefreshCw,
  Clock,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
} from "lucide-react";

const STATE_ORDER = [
  "levi_preliminary",
  "recapture_requested",
  "left_preliminary",
  "contractor_corrected",
  "accepted_levi",
  "contractor_rejected",
];

const STATE_LABEL_KEY = {
  levi_preliminary: "vi02.state.levi_preliminary",
  accepted_levi: "vi02.state.accepted_levi",
  contractor_corrected: "vi02.state.contractor_corrected",
  contractor_rejected: "vi02.state.contractor_rejected",
  recapture_requested: "vi02.state.recapture_requested",
  left_preliminary: "vi02.state.left_preliminary",
};

const STATE_BADGE_CLASS = {
  levi_preliminary: "bg-amber-50 text-amber-900 border-amber-200",
  accepted_levi: "bg-emerald-50 text-emerald-800 border-emerald-200",
  contractor_corrected: "bg-emerald-50 text-emerald-800 border-emerald-200",
  contractor_rejected: "bg-slate-100 text-slate-700 border-slate-200",
  recapture_requested: "bg-sky-50 text-sky-800 border-sky-200",
  left_preliminary: "bg-slate-50 text-slate-700 border-slate-200",
};

const PRIMARY_EVIDENCE_ICON = {
  video_and_voice: Video,
  video: Video,
  voice: Mic,
  photo: Camera,
  calculation: Calculator,
  unknown_provenance: Info,
};

const PRIMARY_EVIDENCE_LABEL_KEY = {
  video_and_voice: "vi02.evidence.video_and_voice",
  video: "vi02.evidence.video",
  voice: "vi02.evidence.voice",
  photo: "vi02.evidence.photo",
  calculation: "vi02.evidence.calculation",
  unknown_provenance: "vi02.evidence.unknown_provenance",
};

const fmtNumber = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return Number.isInteger(n) ? String(n) : (Math.round(n * 100) / 100).toString();
};

const ConfirmDialog = ({ open, title, message, onCancel, onConfirm,
                        confirmLabel, saving, testid, tone = "primary" }) => {
  if (!open) return null;
  const toneClass = tone === "danger"
    ? "bg-red-600 hover:bg-red-700"
    : "bg-[var(--jc-blue)] hover:opacity-90";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
         data-testid={testid || "vi02-confirm-dialog"}>
      <div className="w-full max-w-md rounded-lg bg-white shadow-lg p-5">
        <div className="text-base font-medium mb-2">{title}</div>
        <div className="text-sm text-[var(--jc-text-soft)] mb-4">{message}</div>
        <div className="flex justify-end gap-2">
          <button className="px-3 py-1.5 rounded border text-sm"
                  onClick={onCancel} disabled={saving}
                  data-testid={`${testid || "vi02-confirm"}-cancel`}>
            Cancel
          </button>
          <button className={`px-3 py-1.5 rounded text-white text-sm inline-flex items-center gap-1.5 ${toneClass}`}
                  onClick={onConfirm} disabled={saving}
                  data-testid={`${testid || "vi02-confirm"}-confirm`}>
            {saving && <Loader2 size={14} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const EvidenceBadges = ({ candidate, t }) => {
  const evidence = candidate.evidence || [];
  const kinds = new Set(evidence.map((e) => e?.media_ref?.kind).filter(Boolean));
  const items = [];
  if (kinds.has("video_segment")) items.push({ key: "video", icon: Video });
  if (kinds.has("photo")) items.push({ key: "photo", icon: Camera });
  if ((candidate.source || "").toLowerCase().includes("voice")
      || (candidate.source || "").toLowerCase() === "contractor_stated") {
    items.push({ key: "voice", icon: Mic });
  }
  if (!items.length) return null;
  return (
    <div className="flex items-center gap-1.5 text-xs text-[var(--jc-text-soft)]"
         data-testid={`vi02-evidence-badges-${candidate.id}`}>
      {items.map((it) => (
        <span key={it.key}
              className="inline-flex items-center gap-1 rounded-full bg-slate-50 border border-slate-200 px-2 py-0.5"
              data-testid={`vi02-evidence-badge-${it.key}-${candidate.id}`}>
          <it.icon size={11} />
          {t(`vi02.evidence.${it.key}`)}
        </span>
      ))}
    </div>
  );
};

const CandidateCard = ({ candidate, t, onAccept, onCorrect,
                        onReject, onRecapture, onLeave, busy }) => {
  const [expanded, setExpanded] = useState(false);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [correctValue, setCorrectValue] = useState("");
  const [correctUnit, setCorrectUnit] = useState(candidate.unit || "");
  const [correctReason, setCorrectReason] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [recaptureReason, setRecaptureReason] = useState("");
  const [confirm, setConfirm] = useState(null); // "accept" | "reject" | "recapture" | "leave"

  const state = candidate.decision_state || "levi_preliminary";
  const readOnly = ["accepted_levi", "contractor_corrected",
                    "contractor_rejected"].includes(state)
                   && !candidate.superseded_by_candidate_id;
  const primaryEvidence = candidate.primary_evidence_kind || "unknown_provenance";
  const PrimaryIcon = PRIMARY_EVIDENCE_ICON[primaryEvidence] || Info;

  const submitCorrect = () => {
    const v = Number(correctValue);
    if (!Number.isFinite(v) || v < 0) {
      toast.error(t("vi02.errors.invalid_value"));
      return;
    }
    if (!correctUnit) {
      toast.error(t("vi02.errors.invalid_unit"));
      return;
    }
    // Slice VI-02 pre-QA alignment · Correct now routes through the
    // same modal confirmation primitive used by Accept / Reject /
    // Recapture / Leave Preliminary. Clicking "Save correction"
    // opens the final review; the mutation only fires after the
    // contractor confirms in the modal.
    setConfirm("correct");
  };

  const performCorrect = () => {
    const v = Number(correctValue);
    onCorrect(candidate, {
      approved_value: v,
      approved_unit: correctUnit,
      reason: correctReason || undefined,
    });
    setCorrectOpen(false);
  };

  return (
    <div className="border rounded-lg p-3 mb-2 bg-white"
         data-testid={`vi02-candidate-${candidate.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${STATE_BADGE_CLASS[state] || ""}`}
                  data-testid={`vi02-state-badge-${candidate.id}`}>
              {t(STATE_LABEL_KEY[state] || "vi02.state.levi_preliminary")}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-[var(--jc-text-soft)]"
                  data-testid={`vi02-primary-evidence-${candidate.id}`}>
              <PrimaryIcon size={12} />
              {t(PRIMARY_EVIDENCE_LABEL_KEY[primaryEvidence] || "vi02.evidence.unknown_provenance")}
            </span>
            {candidate.confidence && (
              <span className="text-[11px] text-[var(--jc-text-soft)]"
                    data-testid={`vi02-confidence-${candidate.id}`}>
                {t(`vi02.confidence.${candidate.confidence}`)}
              </span>
            )}
          </div>
          <div className="mt-1 font-medium text-sm"
               data-testid={`vi02-label-${candidate.id}`}>
            {candidate.label || t("vi02.candidate.unnamed")}
          </div>
          <div className="text-sm text-[var(--jc-text-soft)]"
               data-testid={`vi02-values-${candidate.id}`}>
            {t("vi02.values.levi_proposed")}:{" "}
            <strong>{fmtNumber(candidate.quantity)}</strong>{" "}
            {candidate.unit || ""}
            {candidate.contractor_approved_value !== null &&
             candidate.contractor_approved_value !== undefined && (
              <>
                {" · "}
                {t("vi02.values.contractor_approved")}:{" "}
                <strong data-testid={`vi02-approved-value-${candidate.id}`}>
                  {fmtNumber(candidate.contractor_approved_value)}
                </strong>{" "}
                {candidate.contractor_approved_unit || ""}
              </>
            )}
          </div>
          <div className="mt-1.5"><EvidenceBadges candidate={candidate} t={t} /></div>
          {candidate.promotion_blocked_reason && !candidate.downstream_eligible && (
            <div className="mt-1.5 text-[11px] text-[var(--jc-text-soft)] inline-flex items-center gap-1"
                 data-testid={`vi02-blocked-reason-${candidate.id}`}>
              <Info size={11} />
              {t(`vi02.blocked.${candidate.promotion_blocked_reason}`)}
            </div>
          )}
          {candidate.decision_notes && (
            <div className="mt-1.5 text-xs italic text-[var(--jc-text-soft)]"
                 data-testid={`vi02-notes-${candidate.id}`}>
              &ldquo;{candidate.decision_notes}&rdquo;
            </div>
          )}
        </div>
        <button className="p-1 text-[var(--jc-text-soft)]"
                onClick={() => setExpanded((v) => !v)}
                data-testid={`vi02-expand-${candidate.id}`}
                aria-label={t("vi02.actions.toggle_details")}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 space-y-2 text-xs text-[var(--jc-text-soft)]"
             data-testid={`vi02-details-${candidate.id}`}>
          {candidate.recapture_guidance?.guidance && (
            <div className="p-2 rounded bg-sky-50 border border-sky-200 text-sky-900"
                 data-testid={`vi02-recapture-guidance-${candidate.id}`}>
              <div className="inline-flex items-center gap-1.5 font-medium">
                <RefreshCw size={12} />
                {t("vi02.recapture.guidance_title")}
              </div>
              <div className="mt-0.5">{candidate.recapture_guidance.guidance}</div>
              {candidate.recapture_guidance.prefer_video && (
                <div className="mt-0.5 italic text-[11px]">
                  {t("vi02.recapture.prefer_video_note")}
                </div>
              )}
            </div>
          )}
          <div>{t("vi02.details.dimension_kind")}: {candidate.dimension_kind || "—"}</div>
          {candidate.session_id && (
            <div data-testid={`vi02-session-ref-${candidate.id}`}>
              {t("vi02.details.session_ref")}: {candidate.session_id}
            </div>
          )}
          {(candidate.evidence || []).length > 0 && (
            <div>
              <div className="font-medium">{t("vi02.details.evidence_list")}</div>
              <ul className="mt-1 space-y-0.5">
                {(candidate.evidence || []).map((ev, i) => (
                  <li key={i} className="pl-2">
                    · {ev?.media_ref?.kind || "evidence"}
                    {ev?.media_ref?.id ? ` (${ev.media_ref.id})` : ""}
                    {typeof ev?.t_start === "number" ? ` @ ${ev.t_start}s` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {!readOnly && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50"
                  onClick={() => setConfirm("accept")}
                  disabled={busy}
                  data-testid={`vi02-accept-btn-${candidate.id}`}>
            <CheckCircle2 size={14} />
            {t("vi02.actions.accept")}
          </button>
          <button className="inline-flex items-center gap-1 px-3 py-1.5 rounded border text-sm hover:bg-slate-50 disabled:opacity-50"
                  onClick={() => setCorrectOpen(true)}
                  disabled={busy}
                  data-testid={`vi02-correct-btn-${candidate.id}`}>
            <Pencil size={14} />
            {t("vi02.actions.correct")}
          </button>
          <button className="inline-flex items-center gap-1 px-3 py-1.5 rounded border text-sm hover:bg-slate-50 disabled:opacity-50"
                  onClick={() => setConfirm("recapture")}
                  disabled={busy}
                  data-testid={`vi02-recapture-btn-${candidate.id}`}>
            <RefreshCw size={14} />
            {t("vi02.actions.recapture")}
          </button>
          <button className="inline-flex items-center gap-1 px-3 py-1.5 rounded border text-sm hover:bg-slate-50 disabled:opacity-50"
                  onClick={() => setConfirm("leave")}
                  disabled={busy}
                  data-testid={`vi02-leave-btn-${candidate.id}`}>
            <Clock size={14} />
            {t("vi02.actions.leave_preliminary")}
          </button>
          <button className="inline-flex items-center gap-1 px-3 py-1.5 rounded border text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
                  onClick={() => setConfirm("reject")}
                  disabled={busy}
                  data-testid={`vi02-reject-btn-${candidate.id}`}>
            <XCircle size={14} />
            {t("vi02.actions.reject")}
          </button>
        </div>
      )}

      {correctOpen && (
        <div className="mt-3 p-3 rounded border bg-slate-50"
             data-testid={`vi02-correct-form-${candidate.id}`}>
          <div className="text-sm font-medium mb-2">{t("vi02.correct.title")}</div>
          <div className="text-xs text-[var(--jc-text-soft)] mb-2">
            {t("vi02.correct.original_preserved_note")}
          </div>
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-xs">
              {t("vi02.correct.approved_value")}
              <input type="number" min="0" step="0.01"
                     className="block mt-0.5 px-2 py-1 border rounded w-28 text-sm"
                     value={correctValue}
                     onChange={(e) => setCorrectValue(e.target.value)}
                     data-testid={`vi02-correct-value-input-${candidate.id}`} />
            </label>
            <label className="text-xs">
              {t("vi02.correct.approved_unit")}
              <select className="block mt-0.5 px-2 py-1 border rounded text-sm"
                      value={correctUnit}
                      onChange={(e) => setCorrectUnit(e.target.value)}
                      data-testid={`vi02-correct-unit-input-${candidate.id}`}>
                {["ft","in","sqft","linear_ft","count","each","ft_in"].map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </label>
            <input type="text" placeholder={t("vi02.correct.reason_placeholder")}
                   className="flex-1 min-w-[8rem] px-2 py-1 border rounded text-sm"
                   value={correctReason}
                   onChange={(e) => setCorrectReason(e.target.value)}
                   data-testid={`vi02-correct-reason-input-${candidate.id}`} />
          </div>
          <div className="mt-2 flex gap-2">
            <button className="px-3 py-1 rounded bg-[var(--jc-blue)] text-white text-sm disabled:opacity-50"
                    onClick={submitCorrect} disabled={busy}
                    data-testid={`vi02-correct-submit-${candidate.id}`}>
              {t("vi02.correct.confirm")}
            </button>
            <button className="px-3 py-1 rounded border text-sm"
                    onClick={() => setCorrectOpen(false)} disabled={busy}
                    data-testid={`vi02-correct-cancel-${candidate.id}`}>
              {t("vi02.actions.cancel")}
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirm === "accept"}
        title={t("vi02.confirm.accept_title")}
        message={t("vi02.confirm.accept_message")}
        confirmLabel={t("vi02.actions.accept")}
        saving={busy}
        testid={`vi02-confirm-accept-${candidate.id}`}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { setConfirm(null); onAccept(candidate); }} />

      <ConfirmDialog
        open={confirm === "reject"}
        title={t("vi02.confirm.reject_title")}
        message={t("vi02.confirm.reject_message")}
        confirmLabel={t("vi02.actions.reject")}
        saving={busy}
        tone="danger"
        testid={`vi02-confirm-reject-${candidate.id}`}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          onReject(candidate, { reason: rejectReason || undefined });
        }} />

      <ConfirmDialog
        open={confirm === "recapture"}
        title={t("vi02.confirm.recapture_title")}
        message={t("vi02.confirm.recapture_message")}
        confirmLabel={t("vi02.actions.recapture")}
        saving={busy}
        testid={`vi02-confirm-recapture-${candidate.id}`}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          onRecapture(candidate, { reason: recaptureReason || undefined,
                                    prefer_video: true });
        }} />

      <ConfirmDialog
        open={confirm === "leave"}
        title={t("vi02.confirm.leave_title")}
        message={t("vi02.confirm.leave_message")}
        confirmLabel={t("vi02.actions.leave_preliminary")}
        saving={busy}
        testid={`vi02-confirm-leave-${candidate.id}`}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { setConfirm(null); onLeave(candidate); }} />

      {confirm === "correct" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
             data-testid={`vi02-confirm-correct-${candidate.id}`}>
          <div className="w-full max-w-lg rounded-lg bg-white shadow-lg p-5">
            <div className="text-base font-medium mb-3">
              {t("vi02.confirm.correct_title")}
            </div>
            <div className="rounded border bg-slate-50 p-3 text-sm space-y-1.5 mb-3"
                 data-testid={`vi02-confirm-correct-diff-${candidate.id}`}>
              <div className="flex justify-between">
                <span className="text-[var(--jc-text-soft)]">
                  {t("vi02.confirm.correct_original_label")}
                </span>
                <span data-testid={`vi02-confirm-correct-original-${candidate.id}`}>
                  {fmtNumber(candidate.quantity)} {candidate.unit || ""}
                </span>
              </div>
              <div className="flex justify-between font-medium">
                <span>{t("vi02.confirm.correct_new_label")}</span>
                <span data-testid={`vi02-confirm-correct-new-${candidate.id}`}>
                  {fmtNumber(Number(correctValue))} {correctUnit}
                </span>
              </div>
              <div className="flex justify-between text-xs text-[var(--jc-text-soft)] mt-2">
                <span>{t("vi02.confirm.correct_context_label")}</span>
                <span data-testid={`vi02-confirm-correct-context-${candidate.id}`}>
                  {candidate.label || t("vi02.candidate.unnamed")}
                </span>
              </div>
            </div>
            <div className="text-sm text-[var(--jc-text-soft)] space-y-1 mb-4">
              <div>· {t("vi02.confirm.correct_becomes_trusted")}</div>
              <div data-testid={`vi02-confirm-correct-no-estimate-${candidate.id}`}>
                · {t("vi02.confirm.correct_no_estimate")}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button className="px-3 py-1.5 rounded border text-sm"
                      onClick={() => setConfirm(null)} disabled={busy}
                      data-testid={`vi02-confirm-correct-cancel-${candidate.id}`}>
                {t("vi02.actions.cancel")}
              </button>
              <button className="px-3 py-1.5 rounded text-white text-sm inline-flex items-center gap-1.5 bg-[var(--jc-blue)] hover:opacity-90 disabled:opacity-50"
                      onClick={() => { setConfirm(null); performCorrect(); }}
                      disabled={busy}
                      data-testid={`vi02-confirm-correct-confirm-${candidate.id}`}>
                {busy && <Loader2 size={14} className="animate-spin" />}
                {t("vi02.confirm.correct_confirm_label")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const LeviCandidateReviewSection = ({ projectId, onPromotedChange }) => {
  const { t } = useLang();
  const [candidates, setCandidates] = useState([]);
  const [countByState, setCountByState] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showResolved, setShowResolved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/projects/${projectId}/measurement-candidates`
      );
      setCandidates(Array.isArray(data?.candidates) ? data.candidates : []);
      setCountByState(data?.count_by_state || {});
    } catch {
      // Silent — a project without VI-01 candidates should render as
      // "no pending suggestions". Manual contractor workflow is
      // never blocked by this section.
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (projectId) load(); }, [projectId, load]);

  const mutate = useCallback(async (candidate, action, body) => {
    if (busyId) return;
    setBusyId(candidate.id);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/measurement-candidates/${candidate.id}/${action}`,
        body || {},
      );
      setCandidates((prev) => prev.map((c) => (
        c.id === candidate.id
          ? { ...c, ...data }
          : c
      )));
      toast.success(t(`vi02.toast.${action}_success`));
      onPromotedChange && onPromotedChange();
      // Refresh full list — supersession may have added a new row.
      load();
    } catch (err) {
      const code = err?.response?.data?.detail?.error;
      const key = code ? `vi02.errors.${code}` : `vi02.errors.${action}_failed`;
      const msg = t(key);
      toast.error(msg && !msg.startsWith("vi02.errors.")
        ? msg
        : t("vi02.errors.generic"));
    } finally {
      setBusyId(null);
    }
  }, [busyId, projectId, t, onPromotedChange, load]);

  const onAccept = (c) => mutate(c, "accept", {});
  const onCorrect = (c, body) => mutate(c, "correct", body);
  const onReject = (c, body) => mutate(c, "reject", body);
  const onRecapture = (c, body) => mutate(c, "recapture", body);
  const onLeave = (c) => mutate(c, "leave-preliminary", {});

  const { pending, resolved } = useMemo(() => {
    const p = [];
    const r = [];
    for (const c of candidates) {
      const s = c.decision_state || "levi_preliminary";
      if (s === "accepted_levi" || s === "contractor_corrected"
          || s === "contractor_rejected") {
        r.push(c);
      } else {
        p.push(c);
      }
    }
    // Order within each bucket by our canonical STATE_ORDER.
    const rank = (c) => STATE_ORDER.indexOf(c.decision_state || "levi_preliminary");
    p.sort((a, b) => rank(a) - rank(b));
    r.sort((a, b) => rank(a) - rank(b));
    return { pending: p, resolved: r };
  }, [candidates]);

  if (loading && !candidates.length) {
    return (
      <div className="text-xs text-[var(--jc-text-soft)] mb-4"
           data-testid="vi02-loading">
        <Loader2 size={12} className="inline animate-spin mr-1" />
        {t("vi02.section.loading")}
      </div>
    );
  }
  if (!candidates.length) {
    // Nothing from VI-01 yet — the manual WorkAreas workflow is fully
    // unblocked. Levi politely suggests a guided video walkthrough
    // without demanding one (per product hierarchy).
    return (
      <div className="mb-4 p-3 rounded border bg-slate-50 text-xs text-[var(--jc-text-soft)]"
           data-testid="vi02-empty-hint">
        {t("vi02.section.empty_recommend_video")}
      </div>
    );
  }

  return (
    <div className="mb-4" data-testid="vi02-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="font-medium text-sm">{t("vi02.section.title")}</div>
          <span className="text-xs text-[var(--jc-text-soft)]"
                data-testid="vi02-pending-count">
            {t("vi02.section.pending_count", { count: pending.length })}
          </span>
        </div>
        {resolved.length > 0 && (
          <button className="text-xs text-[var(--jc-blue)] underline"
                  onClick={() => setShowResolved((v) => !v)}
                  data-testid="vi02-toggle-resolved">
            {showResolved
              ? t("vi02.section.hide_resolved")
              : t("vi02.section.show_resolved", { count: resolved.length })}
          </button>
        )}
      </div>
      <div className="text-[11px] text-[var(--jc-text-soft)] mb-2"
           data-testid="vi02-hierarchy-note">
        {t("vi02.section.hierarchy_note")}
      </div>
      {pending.map((c) => (
        <CandidateCard
          key={c.id}
          candidate={c}
          t={t}
          busy={busyId === c.id}
          onAccept={onAccept}
          onCorrect={onCorrect}
          onReject={onReject}
          onRecapture={onRecapture}
          onLeave={onLeave} />
      ))}
      {showResolved && resolved.map((c) => (
        <CandidateCard
          key={c.id}
          candidate={c}
          t={t}
          busy={busyId === c.id}
          onAccept={onAccept}
          onCorrect={onCorrect}
          onReject={onReject}
          onRecapture={onRecapture}
          onLeave={onLeave} />
      ))}
    </div>
  );
};

export default LeviCandidateReviewSection;
